// Web Push client helpers.
// The service worker lives at /sw.js; once registered it receives
// VAPID-signed pushes and calls showNotification on our behalf.

import { api } from "@/lib/api";

const SW_PATH = "/sw.js";

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const pushPermission = () => {
  if (!pushSupported()) return "denied";
  return Notification.permission;
};

// Lazy getter for the VAPID public key.
let vapidKeyPromise = null;
const getVapidKey = () => {
  if (!vapidKeyPromise) {
    vapidKeyPromise = api.pushVapidKey().then((d) => d.public_key);
  }
  return vapidKeyPromise;
};

const urlB64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const normalized = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

const registerSw = async () => {
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_PATH);
};

// Returns true on success. May throw with a user-facing message.
export async function enablePush(viewerId) {
  if (!pushSupported()) {
    throw new Error("Notifications aren't supported in this browser.");
  }
  // Standalone is required on iOS, but other platforms work in a regular tab.
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    throw new Error("Notifications blocked. Enable them in your browser settings.");
  }
  const reg = await registerSw();
  const vapidKey = await getVapidKey();
  if (!vapidKey) throw new Error("Push isn't configured on the server.");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(vapidKey),
    });
  }
  const body = sub.toJSON();
  await api.pushSubscribe({
    endpoint: body.endpoint,
    p256dh: body.keys.p256dh,
    auth: body.keys.auth,
    member_id: viewerId || null,
    user_agent: navigator.userAgent.slice(0, 380),
  });
  return true;
}

/**
 * Idempotent "make sure the server knows about my local push subscription".
 * Called from the Settings panel when the user taps "Send test push" but the
 * server reports zero active devices — it's almost always because an earlier
 * subscribe call died in transit, leaving the browser with a live sub the
 * backend never saw. Safe to call multiple times: the backend upserts by
 * endpoint.
 */
export async function resyncPush(viewerId) {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  const body = sub.toJSON();
  try {
    await api.pushSubscribe({
      endpoint: body.endpoint,
      p256dh: body.keys.p256dh,
      auth: body.keys.auth,
      member_id: viewerId || null,
      user_agent: navigator.userAgent.slice(0, 380),
    });
    return true;
  } catch {
    return false;
  }
}

export async function disablePush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  try {
    await api.pushUnsubscribe({ endpoint });
  } catch { /* noop */ }
}

export async function pushSubscribedHere() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return false;
  return !!(await reg.pushManager.getSubscription());
}

// This device's current push endpoint (used to fetch / set its
// notification mode without going through a full re-subscribe).
export async function pushEndpointHere() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return null;
  const sub = await reg.pushManager.getSubscription();
  return sub ? sub.endpoint : null;
}

// Fetch the current notification mode ("events" / "digest") for this
// device. Returns "events" as the safe default when nothing is known.
export async function getNotificationMode() {
  const endpoint = await pushEndpointHere();
  if (!endpoint) return "events";
  try {
    const res = await api.pushGetNotificationMode(endpoint);
    return res?.notification_mode === "digest" ? "digest" : "events";
  } catch {
    return "events";
  }
}

// Per-device audience filter — read/write the push subscription's
// `push_audience` and `skip_imports` fields. Both are optional in
// the payload so a caller can update one without touching the
// other. The 404 fallback re-syncs and retries (same pattern as
// notification-mode).
export async function getPushAudience() {
  const endpoint = await pushEndpointHere();
  if (!endpoint) return { push_audience: "all_family", skip_imports: false };
  try {
    const res = await api.pushGetAudience(endpoint);
    return {
      push_audience: res?.push_audience || "all_family",
      skip_imports: !!res?.skip_imports,
    };
  } catch {
    return { push_audience: "all_family", skip_imports: false };
  }
}

export async function setPushAudience(updates, viewerId) {
  const endpoint = await pushEndpointHere();
  if (!endpoint) return false;
  try {
    await api.pushSetAudience(endpoint, updates);
    return true;
  } catch (err) {
    if (err?.response?.status === 404) {
      const ok = await resyncPush(viewerId);
      if (ok) {
        try {
          await api.pushSetAudience(endpoint, updates);
          return true;
        } catch { /* fall through */ }
      }
    }
    return false;
  }
}
export async function setNotificationMode(mode, viewerId) {
  const endpoint = await pushEndpointHere();
  if (!endpoint) return false;
  try {
    await api.pushSetNotificationMode(endpoint, mode);
    return true;
  } catch (err) {
    if (err?.response?.status === 404) {
      // Re-register, then set the mode in one shot via the subscribe
      // payload — the upsert path persists `notification_mode` too.
      const ok = await resyncPush(viewerId);
      if (ok) {
        try {
          await api.pushSetNotificationMode(endpoint, mode);
          return true;
        } catch { /* fall through */ }
      }
    }
    return false;
  }
}

// Badging API — called from the app when the user lands / refocuses, so
// the home-screen dot goes away even if they swiped the notification.
export async function clearAppBadge() {
  try {
    if (navigator.clearAppBadge) {
      await navigator.clearAppBadge();
    }
  } catch { /* noop */ }
  // Also message the SW to dismiss any lingering notifications it owns.
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
      if (reg && reg.active) {
        reg.active.postMessage({ type: "FAMFEEL_CLEAR_BADGE" });
      }
    }
  } catch { /* noop */ }
}
